import type { CustomerFormData } from '../types';

/**
 * Helper function to determine which modal to open (Add or Edit)
 * @param initialData - Customer data, if exists opens EditCustomerModal
 * @returns Boolean indicating if modal should be in edit mode
 */
export const isEditMode = (initialData?: Partial<CustomerFormData>): boolean => {
    return !!(initialData?.customerId);
};

/**
 * Helper to get the appropriate modal title based on edit mode
 * @param isEdit - Boolean indicating edit mode
 * @param t - Translation function
 * @returns Translated modal title
 */
export const getModalTitle = (isEdit: boolean, t: (key: string) => string): string => {
    return isEdit ? t('EDIT_CUSTOMER_MODAL_TITLE') : t('ADD_CUSTOMER_MODAL_TITLE');
};

/**
 * Helper to get the appropriate button text based on edit mode
 * @param isEdit - Boolean indicating edit mode
 * @param isLoading - Boolean indicating loading state
 * @param t - Translation function
 * @returns Translated button text
 */
export const getButtonText = (isEdit: boolean, isLoading: boolean, t: (key: string) => string): string => {
    if (isLoading) {
        return isEdit ? t('UPDATING') : t('CREATING');
    }
    return isEdit ? t('UPDATE_DETAILS') : t('SAVE_DETAILS');
};

/**
 * Helper to get default form state
 * @returns Default CustomerFormData object
 */
export const getDefaultFormState = (): CustomerFormData => ({
    customerId: undefined,
    businessType: '',
    accountType: '',
    customerName: '',
    orgNo: '',
    ssn: '',
    country: 'Sweden',
    city: '',
    zipCode: '',
    addressLine1: '',
    addressLine2: '',
    customerAddress: '',
    hasVATNumber: 'true',
    vatNumber: '',
    vatCountryCode: 'SE',
    customerReference: '',
    contactEmail: '',
    contactPhone: '',
    invoiceDeliveryMethod: '1',
    phoneNumber: '',
    ourElectronicReference: '7230020',
    yourElectronicReference: '',
    globalLocationNumber: '',
    extraField1: '',
    extraField2: '',
    careOf: '',
    // Email delivery checkbox and secondary email - default checked (true)
    useExistingEmail: true,
    secondaryEmail: '',
    // Postal delivery checkbox and address fields - default checked (true)
    useExistingAddress: true,
    postAddress: '',
    postCity: '',
    postCountryCode: '',
    postZipCode: '',
    postCountry: '',
    paymentTerms: '30',
    invoiceCurrency: 'SEK',
    invoiceLanguage: 'Swedish',
    vatDetails: '',
    reference: '',
    isVATRegistered: 'false',
});
